import { useRef, useState } from "react";
import { Edit3, Eye, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import RichTextEditorPro from "@/components/RichTextEditorPro";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageUpload";
import MagazineProfile from "@/components/MagazineProfile";

/**
 * University Profile Sample — v4 (exact PDF layout)
 * Route: /design-sample/university-profile
 *
 * Reference: Grecotel-style directory page shared by the user.
 * Layout:
 *   1. Top 3 photos edge-to-edge
 *   2. Two columns of magazine text below
 *      → EVERYTHING (name, address, phone, email, intro, headings, sub-blocks)
 *        lives INSIDE the two columns. Nothing outside.
 *      → Each column is editable via a full RichTextEditorPro.
 *
 * Two modes:
 *   • Preview (default) — clean magazine render
 *   • Edit               — each column becomes an RTE
 */

const CREAM = "#FAF6EC";
const RULE  = "#E7DCB9";
const INK   = "#0F172A";
const ACCENT = "#B98700";

const DEFAULT_PHOTOS = [
  "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80",
];

/* ---- Seed rich HTML for each column so the sample looks like the PDF ---- */

const DEFAULT_LEFT = `
<div style="display:flex; align-items:center; gap:14px; margin-bottom:8px;">
  <img src="https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?auto=format&fit=crop&w=200&q=80"
       alt="IIT Delhi logo"
       data-align="left"
       style="width: 22%; margin: 0.25rem 1rem 0.5rem 0; border-radius: 999px; float: left; height: auto;" />
  <h1 style="font-family: 'Playfair Display', Georgia, serif; font-size: 2rem; line-height: 1.1; margin: 0; font-weight: 800;">
    Indian Institute of Technology Delhi
  </h1>
</div>

<p style="font-size: 13px; line-height: 1.6; color: #3A3831; margin: 0 0 0.4rem;">
  Hauz Khas, New Delhi<br/>
  Delhi · 110016, India<br/>
  <strong>Tel:</strong> +91-11-2659-1999<br/>
  <strong>Email:</strong> info@iitd.ac.in<br/>
  <strong>Website:</strong> www.iitd.ac.in
</p>

<p>
  IIT Delhi is a self-governing Institute of Eminence tucked inside the green heart
  of South Delhi. Landmark labs, a 250-acre residential ecosystem, and a founding
  role in the JEE Advanced network make it a magnet for India's most ambitious
  engineers, scientists and designers.
</p>

<p>
  Deep industry partnerships with ISRO, DRDO, Airbus, Google and Microsoft turn
  coursework into launchpads for real-world innovation.
</p>

<p style="font-style: italic; color: #5A5545;">
  "Yogaḥ Karmasu Kauśalam" — Excellence in Action.
</p>

<p>
  The IIT Delhi dream scene is the true essence of Indian scientific ambition —
  off the beaten path, celebrating research and rigour close to the sacred halls
  of Hauz Khas.
</p>
`;

const DEFAULT_RIGHT = `
<h2 style="font-family: 'Playfair Display', Georgia, serif; margin: 0 0 0.6rem;">
  WHERE IT ALL BEGAN:
</h2>

<p>
  Born from a bilateral agreement between India and the United Kingdom in 1961,
  IIT Delhi grew from the College of Engineering &amp; Technology into one of
  Asia's foremost research-led institutes. Six decades later, its 320-acre Hauz
  Khas campus houses over 11,000 students, 16 departments and six centres of
  excellence spanning quantum computing, biosciences and public policy.
</p>

<h3 style="font-family: 'Playfair Display', Georgia, serif; margin: 1.1rem 0 0.4rem;">
  School of Information Technology:
</h3>
<p>
  One of India's most sought-after CSE hubs — offering M.Tech + PhD tracks across
  AI, cyber-physical systems and networked systems. Graduates ship code at OpenAI,
  Google DeepMind, Adobe Research and every unicorn worth naming.
</p>

<h3 style="font-family: 'Playfair Display', Georgia, serif; margin: 1.1rem 0 0.4rem;">
  Bharti School of Telecommunication:
</h3>
<p>
  A joint Bharti–IIT initiative born to build India's 5G/6G talent pipeline. Runs
  an interdisciplinary M.Tech in Telecommunication Technology and Management with
  placements across Nokia Bell Labs, Qualcomm, Ericsson and Reliance Jio.
</p>

<h3 style="font-family: 'Playfair Display', Georgia, serif; margin: 1.1rem 0 0.4rem;">
  Kusuma School of Biological Sciences:
</h3>
<p>
  Twelve labs decoding structural biology, computational genomics and molecular
  medicine. Home to India's first Cryo-EM facility and a fast-track integrated
  PhD that has produced 40+ Wellcome-Trust and DBT fellows since 2011.
</p>
`;

export default function UniversityProfileSampleV4() {
  const [mode, setMode] = useState("preview"); // "preview" | "edit"
  const [photos, setPhotos] = useState(DEFAULT_PHOTOS);
  const [leftHtml,  setLeftHtml]  = useState(DEFAULT_LEFT);
  const [rightHtml, setRightHtml] = useState(DEFAULT_RIGHT);
  const [title, setTitle] = useState("Indian Institute of Technology Delhi");
  const [year, setYear]  = useState(String(new Date().getFullYear()));
  const [brand, setBrand] = useState("EduTime Today");
  const [busyIdx, setBusyIdx] = useState(-1);
  const fileRefs = [useRef(null), useRef(null), useRef(null)];

  const replacePhoto = async (idx, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setBusyIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(prev => prev.map((p, i) => i === idx ? data.url : p));
      toast.success("Image updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusyIdx(-1);
      if (fileRefs[idx].current) fileRefs[idx].current.value = "";
    }
  };

  return (
    <div style={{ background: CREAM, minHeight: "100vh" }}>
      {/* Floating mode toggle (design sample only) */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-6 flex items-center justify-between">
        <div
          className="text-[10px] tracking-[0.32em] font-bold"
          style={{ color: ACCENT }}
        >
          DESIGN SAMPLE · UNIVERSITY PROFILE
        </div>
        <div
          className="inline-flex rounded-full border overflow-hidden text-[12px] font-semibold"
          style={{ borderColor: RULE }}
        >
          <button
            type="button"
            onClick={() => setMode("preview")}
            className="px-4 py-1.5 inline-flex items-center gap-1.5"
            style={{
              background: mode === "preview" ? INK : "transparent",
              color: mode === "preview" ? "#F0C948" : INK,
            }}
            data-testid="uni-sample-mode-preview"
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
          <button
            type="button"
            onClick={() => setMode("edit")}
            className="px-4 py-1.5 inline-flex items-center gap-1.5"
            style={{
              background: mode === "edit" ? INK : "transparent",
              color: mode === "edit" ? "#F0C948" : INK,
            }}
            data-testid="uni-sample-mode-edit"
          >
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>
        </div>
      </div>

      {/* PREVIEW MODE — render the same MagazineProfile that public profiles use.
          It ships with the "View Full Screen" button. */}
      {mode === "preview" && (
        <MagazineProfile
          photos={photos}
          leftHtml={leftHtml}
          rightHtml={rightHtml}
          masthead={title}
          brand={brand}
          year={year}
          filename={title}
          testId="uni-sample"
        />
      )}

      {/* EDIT MODE — 3 replaceable photos + 2 rich-text editors */}
      {mode === "edit" && (
        <>
          {/* Masthead editable fields */}
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.28em] font-bold uppercase" style={{ color: ACCENT }}>
                Top-left name (masthead)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="University / College name"
                data-testid="uni-sample-title-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border bg-white text-sm"
                style={{ borderColor: RULE }}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] tracking-[0.28em] font-bold uppercase" style={{ color: ACCENT }}>Brand</label>
                <input
                  type="text"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  data-testid="uni-sample-brand-input"
                  className="mt-1 w-full px-3 py-2 rounded-lg border bg-white text-sm"
                  style={{ borderColor: RULE }}
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.28em] font-bold uppercase" style={{ color: ACCENT }}>Year</label>
                <input
                  type="text"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  data-testid="uni-sample-year-input"
                  className="mt-1 w-full px-3 py-2 rounded-lg border bg-white text-sm"
                  style={{ borderColor: RULE }}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1 max-w-[1200px] mx-auto pt-4 px-6 lg:px-10">
            {photos.map((src, i) => (
              <div key={i} className="relative aspect-[16/9] overflow-hidden rounded-sm group">
                <img
                  src={resolveImageUrl(src)}
                  alt={`campus ${i + 1}`}
                  className="w-full h-full object-cover"
                  data-testid={`uni-sample-photo-${i}`}
                />
                <input
                  ref={fileRefs[i]}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => replacePhoto(i, e.target.files?.[0])}
                  data-testid={`uni-sample-photo-${i}-input`}
                />
                <button
                  type="button"
                  onClick={() => fileRefs[i].current?.click()}
                  disabled={busyIdx === i}
                  className="absolute inset-0 flex items-center justify-center gap-2 bg-slate-900/55 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`uni-sample-photo-${i}-replace`}
                >
                  {busyIdx === i ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                  ) : (
                    <><Upload className="w-4 h-4" /> Replace image {i + 1}</>
                  )}
                </button>
              </div>
            ))}
          </div>

          <div
            className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-10 pb-24 grid grid-cols-1 md:grid-cols-2 gap-12"
            data-testid="uni-sample-columns"
          >
            <RichTextEditorPro
              value={leftHtml}
              onChange={setLeftHtml}
              minHeight="520px"
              testId="uni-sample-left-editor"
            />
            <RichTextEditorPro
              value={rightHtml}
              onChange={setRightHtml}
              minHeight="520px"
              testId="uni-sample-right-editor"
            />
          </div>
        </>
      )}
    </div>
  );
}
